import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Grid } from '@mui/material';
import ArticlePreviewListUI from './ArticlePreviewListUI';
import { useGetArticleList } from '../hooks/use-get-article-list';
import Loader from './LoadingIndicator';
import { ArticleSummary } from '../../shared/types';
import { ArticlePreviewCardSkeleton } from '../containers/client/ArticlePreviewCard';

export interface ArticleListProps {
    batchSize?: number;
    onDataLoaded?: (totalCount: number) => void;
    source: string;
    onlyMySubscriptions?: boolean;
    excludeMySubscriptions?: boolean;
    freeOnly?: boolean;
    isUserGuideOnly?: boolean;
    userGuideScopes?: string[];
    limit?: number;
    showPagination?: boolean;
    displayArticleCount?: boolean;
    keepPreviousResultsWhileLoading?: boolean;
    showInitialLoadingSkeleton?: boolean;
    initialLoadingSkeletonCount?: number;
    showScopeChipOnCards?: boolean;
    scopeLabelByScope?: Record<string, string>;
}

const ArticleList = ({
    batchSize = 9,
    source,
    onlyMySubscriptions,
    excludeMySubscriptions,
    freeOnly,
    isUserGuideOnly,
    userGuideScopes,
    limit,
    onDataLoaded,
    showPagination,
    displayArticleCount = true,
    keepPreviousResultsWhileLoading = false,
    showInitialLoadingSkeleton = false,
    initialLoadingSkeletonCount,
    showScopeChipOnCards = false,
    scopeLabelByScope,
}: ArticleListProps) => {
    const [page, setPage] = useState(0);
    const [displayedArticles, setDisplayedArticles] = useState<
        ArticleSummary[] | null
    >(null);
    const [displayedTotalCount, setDisplayedTotalCount] = useState(0);
    const [displayedHasNextPage, setDisplayedHasNextPage] = useState(false);
    const userGuideScopesDependency = JSON.stringify(userGuideScopes || []);

    const { articles, loading, hasNextPage, fetchMore, totalCount } =
        useGetArticleList({
            batchSize,
            onlyMySubscriptions,
            excludeMySubscriptions,
            freeOnly,
            isUserGuideOnly,
            userGuideScopes,
            page,
        });

    useEffect(() => {
        if (!loading && onDataLoaded) {
            onDataLoaded?.(totalCount ?? 0);
        }
    }, [totalCount, loading, onDataLoaded]);

    useEffect(() => {
        if (!loading) {
            setDisplayedArticles(articles ?? []);
            setDisplayedTotalCount(totalCount ?? 0);
            setDisplayedHasNextPage(!!hasNextPage);
        }
    }, [articles, hasNextPage, loading, totalCount]);

    useEffect(() => {
        setPage(0);
    }, [
        onlyMySubscriptions,
        excludeMySubscriptions,
        freeOnly,
        isUserGuideOnly,
        userGuideScopesDependency,
    ]);

    const shouldKeepPreviousResults =
        keepPreviousResultsWhileLoading &&
        loading &&
        displayedArticles !== null;
    const currentArticles = shouldKeepPreviousResults
        ? displayedArticles
        : articles;
    const currentTotalCount = shouldKeepPreviousResults
        ? displayedTotalCount
        : totalCount;
    const currentHasNextPage = shouldKeepPreviousResults
        ? displayedHasNextPage
        : hasNextPage;

    if (loading && !shouldKeepPreviousResults) {
        if (showInitialLoadingSkeleton) {
            const loadingSkeletonCount =
                initialLoadingSkeletonCount || batchSize;

            return (
                <Grid
                    container
                    spacing={2}
                    data-testid="article-card-skeleton-grid"
                >
                    {Array.from({ length: loadingSkeletonCount }).map(
                        (_, index) => (
                            <Grid
                                key={index}
                                data-testid="article-card-skeleton-item"
                                size={{
                                    xs: 12,
                                    sm: 6,
                                    md: 4,
                                }}
                            >
                                <ArticlePreviewCardSkeleton />
                            </Grid>
                        )
                    )}
                </Grid>
            );
        }

        return <Loader minHeight={128} />;
    }
    if (!currentArticles?.length) return null;

    const limitedArticles =
        limit && currentArticles.length >= limit
            ? currentArticles.slice(0, limit)
            : currentArticles;

    const total =
        limit && currentTotalCount
            ? currentTotalCount > limit
                ? limit
                : currentTotalCount
            : currentTotalCount;

    const displayShowMoreButton: boolean =
        !limit || !!(limit && limitedArticles.length < limit);

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                sx={{
                    opacity: shouldKeepPreviousResults ? 0.55 : 1,
                    transition: 'opacity 180ms ease',
                }}
            >
                <ArticlePreviewListUI
                    source={source}
                    showPagination={showPagination}
                    articles={limitedArticles}
                    total={total}
                    displayShowMoreButton={displayShowMoreButton}
                    displayArticleCount={displayArticleCount}
                    showScopeChipOnCards={showScopeChipOnCards}
                    scopeLabelByScope={scopeLabelByScope}
                    hasNextPage={!!currentHasNextPage}
                    fetchMore={() => {
                        fetchMore();
                        setPage(page + 1);
                    }}
                />
            </Box>
            {shouldKeepPreviousResults && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <CircularProgress size={20} />
                </Box>
            )}
        </Box>
    );
};

export default ArticleList;
